import { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { QrCode, ArrowRight, PhoneCall, Mail, MapPin, Sparkles } from "lucide-react";
import { apiPublic } from "@/lib/api";

export default function PublicShell() {
  const loc = useLocation();
  const isMenuPage = loc.pathname.startsWith("/menu/");

  const [settings, setSettings] = useState({
    support_phone: "+919876543210",
    support_email: "support@tablezy.com",
    location: "Tech Park, Jaipur & Delhi, India",
    company_name: "Tablezy Technologies",
  });

  useEffect(() => {
    apiPublic.get("/public/settings")
      .then(({ data }) => {
        if (data) {
          setSettings((prev) => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, []);

  if (isMenuPage) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background font-body text-foreground">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border/80 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-primary to-accent text-primary-foreground grid place-items-center shadow-md shadow-primary/20 group-hover:scale-105 transition-transform">
              <QrCode className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div>
              <span className="font-heading text-2xl font-bold tracking-tight text-slate-900">Tablezy</span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-primary font-semibold">Digital Menus</span>
            </div>
          </Link>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <Link
              to="/"
              className={`transition-colors hover:text-primary ${loc.pathname === "/" ? "text-primary font-semibold" : "text-slate-600"}`}
            >
              Home
            </Link>
            <Link
              to="/our-work"
              className={`transition-colors hover:text-primary ${loc.pathname === "/our-work" ? "text-primary font-semibold" : "text-slate-600"}`}
            >
              Our Work
            </Link>
            <Link
              to="/contact"
              className={`transition-colors hover:text-primary ${loc.pathname === "/contact" ? "text-primary font-semibold" : "text-slate-600"}`}
            >
              Contact
            </Link>
            <Link
              to="/support"
              className={`transition-colors hover:text-primary ${loc.pathname === "/support" ? "text-primary font-semibold" : "text-slate-600"}`}
            >
              Support
            </Link>
          </nav>

          {/* Right CTA */}
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-2 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all"
            >
              <span>Create Your QR Menu</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-300 pt-16 pb-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-slate-800/80">
            {/* Brand column */}
            <div className="space-y-4 md:col-span-1">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
                  <QrCode className="w-5 h-5 stroke-[2.2]" />
                </div>
                <span className="font-heading text-2xl font-bold text-white">Tablezy</span>
              </Link>
              <p className="text-slate-400 text-sm leading-relaxed">
                “Your Menu. One Scan Away.”<br />
                Empowering cafes & restaurants with modern digital QR menu solutions.
              </p>
              <div className="flex items-center gap-2 text-xs font-mono text-primary bg-primary/10 w-fit px-3 py-1 rounded-full border border-primary/20">
                <Sparkles className="w-3.5 h-3.5" /> Next-Gen Restaurant Tech
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading text-white font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li><Link to="/our-work" className="hover:text-primary transition-colors">Our Work</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Contact Us</Link></li>
                <li><Link to="/support" className="hover:text-primary transition-colors">Support & Help</Link></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-heading text-white font-semibold text-lg mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-primary transition-colors">About Tablezy</Link></li>
                <li><Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-primary transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/faq" className="hover:text-primary transition-colors">FAQ</Link></li>
              </ul>
            </div>

            {/* Dynamic Contact Support */}
            <div className="space-y-3">
              <h4 className="font-heading text-white font-semibold text-lg mb-4">Get In Touch</h4>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <PhoneCall className="w-4 h-4 text-primary shrink-0" />
                <a href={`tel:${settings.support_phone}`} className="hover:text-white transition-colors">
                  {settings.support_phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <Mail className="w-4 h-4 text-primary shrink-0" />
                <a href={`mailto:${settings.support_email}`} className="hover:text-white transition-colors">
                  {settings.support_email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <span>{settings.location}</span>
              </div>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
            <p>© {new Date().getFullYear()} {settings.company_name || "Tablezy Technologies"}. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <span>Fast • Touchless • Smart</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
